/**
 * The FAQ and carrier-network pages are ported straight from the template, where the
 * accordions are driven by Bootstrap's collapse plugin. Only Bootstrap's *CSS* was
 * vendored into this project, so without this the panels are inert: the one item the
 * markup ships with `.show` stays open and every other question is a dead button.
 *
 * This reimplements just the part of the plugin that markup uses, toggle `.show` on the
 * target, keep the trigger's `.collapsed`/`aria-expanded` in sync, and, when the panel
 * declares a `data-bs-parent`, close its siblings so only one stays open per group. The
 * open/close animation itself is CSS (`.at-faq-collapse` animates `grid-template-rows`),
 * so nothing here has to measure or set heights.
 */
export function setupCollapse(root: HTMLElement) {
  const triggers = Array.from(
    root.querySelectorAll<HTMLElement>('[data-bs-toggle="collapse"]')
  );
  if (!triggers.length) return () => {};

  const panelOf = (trigger: HTMLElement) => {
    const selector = trigger.dataset.bsTarget;
    return selector ? root.querySelector<HTMLElement>(selector) : null;
  };

  const setState = (
    trigger: HTMLElement,
    panel: HTMLElement,
    open: boolean
  ) => {
    panel.classList.toggle('show', open);
    trigger.classList.toggle('collapsed', !open);
    trigger.setAttribute('aria-expanded', String(open));
  };

  // The template leaves `aria-controls` off some triggers; derive it so the button and
  // its panel are still associated for assistive tech.
  triggers.forEach((trigger) => {
    const panel = panelOf(trigger);
    if (panel?.id && !trigger.hasAttribute('aria-controls')) {
      trigger.setAttribute('aria-controls', panel.id);
    }
  });

  const onClick = (event: Event) => {
    const trigger = (event.currentTarget as HTMLElement) ?? null;
    const panel = trigger && panelOf(trigger);
    if (!panel) return;

    const opening = !panel.classList.contains('show');
    const parentSelector = panel.dataset.bsParent;

    if (opening && parentSelector) {
      const parent = root.querySelector<HTMLElement>(parentSelector);
      parent
        ?.querySelectorAll<HTMLElement>('.show[data-bs-parent]')
        .forEach((sibling) => {
          if (sibling === panel) return;
          const siblingTrigger = triggers.find(
            (candidate) => panelOf(candidate) === sibling
          );
          if (siblingTrigger) setState(siblingTrigger, sibling, false);
          else sibling.classList.remove('show');
        });
    }

    setState(trigger, panel, opening);
  };

  triggers.forEach((trigger) => trigger.addEventListener('click', onClick));

  return () => {
    triggers.forEach((trigger) =>
      trigger.removeEventListener('click', onClick)
    );
  };
}
